import { useEffect, useMemo, useRef, useState } from 'react';
import { CopyButton } from '@Components/CopyButton';
import { Modal } from 'react-bootstrap';
import { Checkbox } from '@Components/FormCheck';
import { MdExpandLess, MdExpandMore, MdRefresh, MdWarningAmber } from 'react-icons/md';
import { Button } from '../../../Components/Button';
import { InteractiveSpan } from '../../../Components/InteractiveEl';
import { Spinner } from '../../../Components/Spinner';
import { Appearance, Design } from '../../../Components/designConstants';
import {
  FieldSuggestion,
  FIELD_LABEL,
  FIELD_WEIGHT,
  OptimizableFieldType,
  OptimizationResult
} from './ai-optimize-constants';
import { getOptimizableFields, optimizeListing } from './ai-optimize-utils';
import { ProductProposal, SavedSuggestion } from './proposals-utils';
import { formatAiError } from './title-compliance-utils';
import { AuditedProduct } from './scoring-engine-constants';
import { projectRewrittenScore, ProjectedRewrites } from './scoring-engine-utils';

interface FieldPanelProps {
  field: OptimizableFieldType;
  current: string;
  suggestion: FieldSuggestion;
  accepted: boolean;
  onToggleAccept: () => void;
}

const FieldPanel = ({ field, current, suggestion, accepted, onToggleAccept }: FieldPanelProps) => {
  const [reasoningOpen, setReasoningOpen] = useState(false);

  return (
    <div className={`optimize-field-panel${accepted ? ' is-accepted' : ''}`}>
      <div className='optimize-field-panel-header'>
        <span className='optimize-field-panel-title'>
          {FIELD_LABEL[field]}{' '}
          <span className='optimize-field-panel-weight'>{FIELD_WEIGHT[field]}% weight</span>
        </span>
        <Checkbox label='Accept rewrite' checked={accepted} onChange={onToggleAccept} />
      </div>
      <div className='optimize-field-panel-body'>
        <div className='optimize-field-current'>
          <span className='optimize-field-column-label'>Current ({current.length} chars)</span>
          <p>{current || <span className='is-empty'>(none)</span>}</p>
        </div>
        <div className='optimize-field-suggested'>
          <div className='optimize-field-suggested-header'>
            <span className='optimize-field-column-label'>
              Suggested ({suggestion.suggestion.length} chars)
            </span>
            <CopyButton
              label='Copy'
              text={
                field === 'bullets'
                  ? suggestion.suggestion
                      .split('|')
                      .map(b => b.trim())
                      .filter(Boolean)
                      .join('\n')
                  : suggestion.suggestion
              }
            />
          </div>
          {field === 'bullets' ? (
            <ul>
              {suggestion.suggestion
                .split('|')
                .map(b => b.trim())
                .filter(Boolean)
                .map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
            </ul>
          ) : (
            <p>{suggestion.suggestion}</p>
          )}
          <InteractiveSpan
            className='optimize-reasoning-toggle'
            onClick={() => setReasoningOpen(v => !v)}
          >
            {reasoningOpen ? <MdExpandLess /> : <MdExpandMore />}
            {reasoningOpen ? 'Hide reasoning' : 'Why this rewrite?'}
          </InteractiveSpan>
          {reasoningOpen && <p className='optimize-reasoning'>{suggestion.reasoning}</p>}
        </div>
      </div>
    </div>
  );
};

interface OptimizeModalProps {
  product: AuditedProduct;
  existingProposal?: ProductProposal;
  onSaveProposal: (productId: string, proposal: ProductProposal | null) => void;
  onClose: () => void;
}

export const OptimizeModal = ({
  product,
  existingProposal,
  onSaveProposal,
  onClose
}: OptimizeModalProps) => {
  const fieldsToOptimize = useMemo(() => getOptimizableFields(product), [product]);

  const initialResult: OptimizationResult | null = existingProposal
    ? {
        title: existingProposal.title ?? null,
        bullets: existingProposal.bullets ?? null,
        description: existingProposal.description ?? null,
        alexaScore: existingProposal.alexaScore ?? null
      }
    : null;

  const [loading, setLoading] = useState(!existingProposal);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<OptimizationResult | null>(initialResult);
  const [accepted, setAccepted] = useState<Record<OptimizableFieldType, boolean>>({
    title: existingProposal?.title?.accepted ?? false,
    bullets: existingProposal?.bullets?.accepted ?? false,
    description: existingProposal?.description?.accepted ?? false
  });

  // Guards against setState after the modal has closed mid-request — the request
  // itself isn't cancelled (fetch has no cheap abort here), just its effect on state.
  const unmountedRef = useRef(false);
  useEffect(
    () => () => {
      unmountedRef.current = true;
    },
    []
  );

  const runOptimization = (force = false) => {
    setLoading(true);
    setError(null);
    optimizeListing(product, fieldsToOptimize)
      .then(res => {
        if (unmountedRef.current) return;
        setResult(res);
        setLoading(false);
        if (force) {
          setAccepted({ title: false, bullets: false, description: false });
        }
      })
      .catch(err => {
        if (unmountedRef.current) return;
        setError(formatAiError(err));
        setLoading(false);
      });
  };

  useEffect(() => {
    if (existingProposal) return;
    runOptimization();
    // Deliberately omits `existingProposal`/`runOptimization` — this should only
    // fire once on mount (or if a different `product` is passed in), not whenever
    // those closures are recreated by a re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  const persistProposal = () => {
    if (!result) {
      onSaveProposal(product.id, null);
      return;
    }
    const buildSaved = (
      s: FieldSuggestion | null,
      isAccepted: boolean
    ): SavedSuggestion | undefined =>
      s ? { suggestion: s.suggestion, reasoning: s.reasoning, accepted: isAccepted } : undefined;
    const next: ProductProposal = {
      productId: product.id,
      generatedAt: existingProposal?.generatedAt ?? Date.now(),
      title: buildSaved(result.title, accepted.title),
      bullets: buildSaved(result.bullets, accepted.bullets),
      description: buildSaved(result.description, accepted.description),
      ...(result.alexaScore !== null && result.alexaScore !== undefined
        ? { alexaScore: result.alexaScore }
        : {})
    };
    onSaveProposal(product.id, next);
  };

  const handleClose = () => {
    persistProposal();
    onClose();
  };

  const projectedRewrites: ProjectedRewrites = useMemo(() => {
    if (!result) return {};
    const r: ProjectedRewrites = {};
    if (accepted.title && result.title) {
      r.title = result.title.suggestion;
    }
    if (accepted.bullets && result.bullets) {
      r.bullets = result.bullets.suggestion
        .split(/\n+/)
        .map(b => b.replace(/^\s*[-*\d.)\s]+/, '').trim())
        .filter(b => b !== '')
        .join('|');
    }
    if (accepted.description && result.description) {
      r.description = result.description.suggestion;
    }
    return r;
  }, [accepted, result]);

  const projectedProduct = useMemo(
    () => projectRewrittenScore(product, projectedRewrites),
    [product, projectedRewrites]
  );
  const scoreDelta = projectedProduct.finalScore - product.finalScore;

  const currentByField: Record<OptimizableFieldType, string> = {
    title: product.title === ' — ' ? '' : product.title,
    bullets:
      product.featureBullets.length > 0
        ? product.featureBullets.map((b, i) => `${i + 1}. ${b}`).join('\n')
        : '',
    description: product.fullDescription === ' — ' ? '' : product.fullDescription
  };

  return (
    <Modal show centered size='xl' onHide={handleClose} className='scorecard-optimize-modal'>
      <Modal.Header closeButton>
        <div>
          <span className='scorecard-modal-eyebrow'>Alexa for Shopping–Aware AI Optimization</span>
          <Modal.Title>{product.title}</Modal.Title>
          <span className='scorecard-modal-subtitle'>
            {product.asin} · {product.brand}
          </span>
        </div>
        <div className='scorecard-score-delta-hud'>
          <div>
            <span>Current</span>
            <span>{product.finalScore}%</span>
          </div>
          <div>
            <span>Projected</span>
            <span className={scoreDelta > 0 ? 'tone-success' : ''}>
              {projectedProduct.finalScore}%
            </span>
          </div>
          {scoreDelta > 0 && <span className='scorecard-score-delta-badge'>+{scoreDelta}</span>}
        </div>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className='scorecard-modal-loading'>
            <Spinner size={40} />
            <p>Generating Alexa for Shopping–aware rewrites...</p>
          </div>
        )}
        {error && (
          <div className='scorecard-modal-error'>
            <MdWarningAmber />
            <div>
              <p>Optimization failed</p>
              <p>{error}</p>
            </div>
          </div>
        )}
        {!loading && !error && result && fieldsToOptimize.length === 0 && (
          <div className='scorecard-modal-success-banner'>
            <p>This listing is already optimized</p>
            <p>
              All three rewritable fields (title, bullets, description) are scoring at full marks.
            </p>
          </div>
        )}
        {!loading &&
          !error &&
          result &&
          fieldsToOptimize.map(field => {
            const suggestion = result[field];
            if (!suggestion) return null;
            return (
              <FieldPanel
                key={field}
                field={field}
                current={currentByField[field]}
                suggestion={suggestion}
                accepted={accepted[field]}
                onToggleAccept={() => setAccepted(prev => ({ ...prev, [field]: !prev[field] }))}
              />
            );
          })}
      </Modal.Body>
      <Modal.Footer>
        <p className='scorecard-modal-footnote'>
          Suggestions persist as proposals against this ASIN. Use Export AI Rewrites to hand them to
          the content team.
        </p>
        {(result !== null || error !== null) && !loading && (
          <Button
            appearance={Appearance.OUTLINED}
            design={Design.NEUTRAL}
            leadingElement={<MdRefresh />}
            onClick={() => runOptimization(true)}
          >
            {error && !result ? 'Try again' : 'Regenerate'}
          </Button>
        )}
        <Button onClick={handleClose}>Save & Close</Button>
      </Modal.Footer>
    </Modal>
  );
};
